import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatWhen, loadSiteInfo } from "../../lib/siteInfo";
import { LaunchCard, inputClass, inputStyle } from "./ui";

function NoteForm({ suggested, onSaved }) {
  const [form, setForm] = useState({ version: suggested, title: "", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm((f) => ({ ...f, version: suggested })); }, [suggested]);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/release-notes", form);
      toast.success(data.stamped ? `Release ${data.version} written; this build is now stamped ${data.version}` : `Release ${data.version} written`);
      setForm({ version: "", title: "", notes: "" });
      loadSiteInfo(true);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid gap-3 md:grid-cols-[140px_1fr] mb-5" data-testid="release-note-form">
      <div><label className="field-label">Version</label>
        <input className={inputClass} style={inputStyle} value={form.version} onChange={set("version")} data-testid="release-note-version-input" /></div>
      <div><label className="field-label">Title</label>
        <input className={inputClass} style={inputStyle} value={form.title} onChange={set("title")} placeholder="e.g. Vendor filter on the invoice queue" data-testid="release-note-title-input" /></div>
      <div className="md:col-span-2"><label className="field-label">What changed</label>
        <textarea rows={3} className={inputClass} style={inputStyle} value={form.notes} onChange={set("notes")} data-testid="release-note-body-input" /></div>
      <div className="md:col-span-2"><button type="button" className="btn-primary" disabled={busy || !form.version || form.title.length < 3 || form.notes.length < 3} onClick={save} data-testid="release-note-save-btn">{busy ? "Saving…" : "Write release note"}</button></div>
    </div>
  );
}

export function ReleaseNotesCard({ onSaved }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/admin/release-notes").then(({ data: d }) => setData(d))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const canWrite = user?.permissions?.includes("system") && data.environment !== "production";
  return (
    <LaunchCard eyebrow="Release notes" title="What each version contains" testId="release-notes-card">
      <p className="text-sm mb-4 max-w-3xl" style={{ color: "var(--muted)" }}>
        {data.environment === "production"
          ? "Release notes are written on dev-portal and carried here through the linked site once the matching version is published."
          : "Write the note before publishing. Its version number becomes this build's version stamp, so production shows which release it runs."}
      </p>
      {canWrite && <NoteForm suggested={data.suggested_version} onSaved={() => { load(); onSaved?.(); }} />}
      <ul className="divide-y max-h-[320px] overflow-auto" style={{ borderColor: "var(--border)" }} data-testid="release-notes-list">
        {data.notes.map((n) => (
          <li key={n.version} className="py-3 text-sm" data-testid={`release-note-${n.version}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="font-mono font-bold" style={{ color: "var(--ink)" }}>v{n.version}</span>
              <span className="font-semibold" style={{ color: "var(--ink)" }}>{n.title}</span>
              {n.version === data.build.version && <span className="pill text-[9px]">This build</span>}
            </div>
            <p className="mt-1 whitespace-pre-line" style={{ color: "var(--muted)" }}>{n.notes}</p>
            <div className="text-[11px] mt-1" style={{ color: "var(--muted)" }}>{n.created_by} · {formatWhen(n.created_at)}{n.source === "linked site" ? " · from dev-portal" : ""}</div>
          </li>
        ))}
        {!data.notes.length && <li className="py-3 text-sm" style={{ color: "var(--muted)" }}>No release notes yet.</li>}
      </ul>
    </LaunchCard>
  );
}
